const ENV_PREFIX_ = 'VITE_APP_'

/**
 * This helper function is an abstraction of environment variable access.
 * Due to the increasingly non-standard approach used by different builders,
 *  this layer exists to guard the source code against ever-changing approaches to environment naming.
 *
 * According to existing conventions, it is assumed that all environment variable names are uppercased.
 *
 * @param {string} field The name of the environment variable in uppercase. The field name should _not_ be prefixed with the applicable builder prefix.
 *
 * @param {string} prefix _This argument should not be passed._ The prefix used in the builder. It exists only for flexibility and debugging.
 *
 * @param {ImportMetaEnv} environment The environment to use. Defaults to import.meta.env
 *
 * @return {string} The environment value corresponding to the given key.
 *
 * @example
 * // Good: env('MY_ENV_VAR')
 * // Bad:  env('VITE_APP_MY_ENV_VAR')
 */
export default function env(
    field: string,
    prefix: string = ENV_PREFIX_,
    environment: ImportMetaEnv = import.meta.env
): string {
    const value = getEnvValue(field, prefix, environment)
    if (typeof value === 'undefined')
        console.warn(`Field ${field} does not exist in environment`, environment)
    return value
}

const getEnvValue = (field: string, prefix: string, environment: ImportMetaEnv) => {
    field = field.toUpperCase()
    if (!field.startsWith(prefix) && !(field in environment)) return environment[prefix + field]
    return environment[field]
}

export const getEnvString = (field: string, prefix: string = ENV_PREFIX_): string =>
    String(env(field, prefix))
export const getEnvNumber = (field: string, prefix: string = ENV_PREFIX_): number =>
    Number(env(field, prefix))
export const getEnvBoolean = (field: string, prefix: string = ENV_PREFIX_): boolean =>
    Boolean(env(field, prefix))
export const isDevEnv = (): boolean => getEnvBoolean('DEV') && !getEnvBoolean('PROD')
